import { compact, isString, last } from "lodash-es";

import { validationModule } from "@/modules/validation";

type ExtractBlobNameFromDownloadUrlArgs = {
  downloadUrl: string;
};

type ExtractAudioRecordingIdFromBlobNameArgs = {
  blobName: string;
};

type ExtractCapturedAudioIdFromBlobNameArgs = {
  blobName: string;
};

const splitBlobNameParts = (blobName: string): string[] => compact(blobName.split("/"));

export const extractBlobNameFromDownloadUrl = ({
  downloadUrl,
}: ExtractBlobNameFromDownloadUrlArgs): string | null => {
  if (!isString(downloadUrl) || !downloadUrl.trim()) return null;

  try {
    const url = new URL(downloadUrl);
    const path = url.pathname;
    const objectIndex = path.indexOf("/o/");

    if (objectIndex !== -1) {
      return decodeURIComponent(path.slice(objectIndex + 3));
    }

    const parts = splitBlobNameParts(decodeURIComponent(path));
    if (parts.length < 2) return null;
    return parts.slice(1).join("/");
  } catch {
    return null;
  }
};

export const extractCapturedAudioIdFromBlobName = ({
  blobName,
}: ExtractCapturedAudioIdFromBlobNameArgs): string | null => {
  if (!isString(blobName) || !blobName.trim()) return null;

  const parts = splitBlobNameParts(blobName);
  if (parts.length !== 2) return null;

  const candidate = parts[0];
  if (!validationModule.isUuid(candidate)) return null;

  return candidate;
};

export const extractAudioRecordingIdFromBlobName = ({
  blobName,
}: ExtractAudioRecordingIdFromBlobNameArgs): string | null => {
  if (!isString(blobName) || !blobName.trim()) return null;

  if (extractCapturedAudioIdFromBlobName({ blobName })) {
    return null;
  }

  const parts = splitBlobNameParts(blobName);
  const uuidParts = parts.filter(part => validationModule.isUuid(part));
  const candidate = last(uuidParts);

  return candidate ?? null;
};
